// src/components/ws/WsMatrix.jsx
// 出席記録（全画面）の「出欠表」タブ【2026-10-13】
//
//   学生 × 回 の表を画面いっぱいに出す。もとの書き出しプレビューの出席簿に当たる。
//     ・出欠の書き方（記号／文字）・回の見出し（日付／第N回）・文字の大きさは、ここで変える。
//       書き出しと同じ設定に覚えるので、この表の見た目のまま出席簿として書き出される。
//     ・名前を押すと、右にその学生の出欠のまとめが出る。
//     ・マスを押すと、右の欄でその回の出欠を直せる（POST /api/attendance/manual。誰が直したかが残る）。
//       まとめて直す（範囲選択）・申請の確認は「出欠の確認・修正」のページ。
import React, { useEffect, useMemo, useRef, useState } from 'react';
import WsSplit from './WsSplit';
import { useColWidths, ColGrip } from './useColWidths';
import { Link } from 'react-router-dom';
import {
  ATTENDED, FONT_STEPS, FONT_LABELS, MARKS_KEY, HEADING_KEY, FONT_KEY,
  loadMarks, loadHeading, loadFontStep, writePref, statusInfo, fmtDate, fmtMD, rateColor,
} from './wsCommon';

const CHOICES = ['present', 'late', 'early_leave', 'absent', 'excused'];

export default function WsMatrix({ classId, matrix, risk, readOnly, focus, auth, setErr, setMsg, onChanged }) {
  const [query, setQuery]     = useState('');
  const [filter, setFilter]   = useState('all');      // all | risk | outside
  const [marks, setMarksS]    = useState(loadMarks);
  const [heading, setHeadingS] = useState(loadHeading);
  const [fontStep, setFontS]  = useState(loadFontStep);
  const [studentId, setStudentIdRaw] = useState(null);  // 右に出す学生
  // 名前・マスを押した回数。横の欄を畳んでいても、押したら開く（WsSplit の openSignal）
  const [pickN, setPickN] = useState(0);
  const setStudentId = (v) => { setStudentIdRaw(v); if (v != null) setPickN(n => n + 1); };
  const [cell, setCell]       = useState(null);      // { stuId, sesId }
  const [reason, setReason]   = useState('');
  const [busy, setBusy]       = useState(false);
  const [over, setOver]       = useState({});        // 直した直後の表示（読み直すまで）
  const rowRefs = useRef({});

  const setMarks = (v) => { setMarksS(v); writePref(MARKS_KEY, v); };
  const setHeading = (v) => { setHeadingS(v); writePref(HEADING_KEY, v); };
  const changeFont = (d) => {
    const v = Math.max(0, Math.min(FONT_STEPS.length - 1, fontStep + d));
    setFontS(v); writePref(FONT_KEY, v);
  };

  // 概要から「この学生」「要注意だけ」で来たとき
  useEffect(() => {
    if (!focus) return;
    if (focus.filter) setFilter(focus.filter);
    if (focus.student) {
      setStudentId(focus.student); setCell(null);
      setTimeout(() => {
        const el = rowRefs.current[focus.student];
        if (el) el.scrollIntoView({ block: 'center' });
      }, 60);
    }
  }, [focus]);

  // 読み直したら、手元の上書きは捨てる
  useEffect(() => { setOver({}); }, [matrix]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { setCell(null); setReason(''); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const sessions = useMemo(() => (matrix && matrix.sessions) || [], [matrix]);
  // 列の幅（エクセルのように見出しの境目をドラッグ）。回の列はまとめて同じ幅にする【2026-10-13】
  const colKeys = useMemo(() => ['id', 'name', 'rate', 'abs', ...sessions.map(() => 'ses')], [sessions]);
  const cols = useColWidths('matrix', colKeys, { defaultWidth: k => ({ id: 96, name: 130, rate: 72, abs: 56, ses: 46 }[k] || 80) });
  const pin1 = cols.active ? { left: cols.widthOf('id') } : undefined;
  const students = useMemo(() => (matrix && matrix.students) || [], [matrix]);
  const hasRoster = students.some(s => !s.is_outside_roster);
  const outsideCount = hasRoster ? students.filter(s => s.is_outside_roster).length : 0;

  const cellOf = (stu, ses) => {
    const k = stu.id + ':' + ses.id;
    return Object.prototype.hasOwnProperty.call(over, k) ? over[k] : (stu.cells || {})[ses.id];
  };

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return students.filter(s => {
      if (filter === 'risk' && !risk.has(s.id)) return false;
      if (filter === 'outside' && !(hasRoster && s.is_outside_roster)) return false;
      if (!q) return true;
      return String(s.student_id || '').toLowerCase().includes(q) || String(s.name || '').toLowerCase().includes(q);
    });
  }, [students, filter, query, risk, hasRoster]);

  // 回ごとの出席人数（名簿の学生だけ）
  const totals = useMemo(() => sessions.map(ses =>
    students.filter(s => (!hasRoster || !s.is_outside_roster) && ATTENDED.includes((s.cells || {})[ses.id])).length
  ), [sessions, students, hasRoster]);
  const enrolledCount = hasRoster ? students.length - outsideCount : students.length;

  const mark = (st) => {
    const info = statusInfo(st);
    if (!st) return marks === 'symbol' ? '×' : '欠';
    return marks === 'symbol' ? info.symbol : info.short;
  };

  const selStu = students.find(s => s.id === studentId) || null;
  const selSes = cell ? sessions.find(s => s.id === cell.sesId) : null;
  const selCellStu = cell ? students.find(s => s.id === cell.stuId) : null;
  const now = selCellStu && selSes ? cellOf(selCellStu, selSes) : null;

  const pickCell = (stu, ses) => {
    setStudentId(stu.id);
    if (readOnly) return;
    setCell(c => (c && c.stuId === stu.id && c.sesId === ses.id ? null : { stuId: stu.id, sesId: ses.id }));
    setReason('');
  };

  const post = (status, enroll) => fetch('/api/attendance/manual', {
    method: 'POST', headers: auth(),
    body: JSON.stringify({ sessionId: selSes.id, userId: selCellStu.id, status,
                           reason: reason.trim() || undefined, ...(enroll ? { enroll: true } : {}) }),
  }).then(async res => ({ res, d: await res.json().catch(() => ({})) }));

  const save = async (status) => {
    if (!selSes || !selCellStu || busy) return;
    if (now === status) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      let { res, d } = await post(status, false);
      if (res.status === 400 && d.code === 'NOT_ENROLLED') {
        if (!window.confirm(`${selCellStu.name || selCellStu.student_id} さんは履修者名簿にいません。履修者に加えて記録しますか？`)) return;
        ({ res, d } = await post(status, true));
      }
      if (!res.ok || !d.success) throw new Error(d.message || ('HTTP ' + res.status));
      setOver(o => ({ ...o, [selCellStu.id + ':' + selSes.id]: status }));
      setMsg(`${selCellStu.name || selCellStu.student_id} さんの第${selSes.no}回（${fmtMD(selSes.date)}）を「${statusInfo(status).label}」にしました`);
      setCell(null); setReason('');
      if (onChanged) onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!matrix) return <div className="ws-panel"><p className="ws-muted">読み込み中…</p></div>;

  const seg = (on) => 'ws-seg-btn' + (on ? ' on' : '');
  const selRisk = selStu ? risk.get(selStu.id) : null;
  const selCounts = selStu ? (() => {
    const c = { present: 0, late: 0, early_leave: 0, excused: 0, absent: 0 };
    sessions.forEach(ses => {
      const st = cellOf(selStu, ses);
      if (st && c[st] != null) c[st] += 1; else c.absent += 1;
    });
    return c;
  })() : null;

  return (
    <WsSplit id="matrix" label="選んだ学生" openSignal={pickN || null}>
      <div className="ws-split-main">
        <div className="ws-toolbar">
          <input type="search" value={query} onChange={e => setQuery(e.target.value)}
                 placeholder="名前・学籍番号で探す" aria-label="学生を探す" className="ws-search" />
          <div className="ws-seg" role="group" aria-label="絞り込み">
            <button type="button" className={seg(filter === 'all')} onClick={() => setFilter('all')}>全員 {students.length}</button>
            <button type="button" className={seg(filter === 'risk')} onClick={() => setFilter('risk')}>要注意だけ {risk.size}</button>
            {outsideCount > 0 && (
              <button type="button" className={seg(filter === 'outside')} onClick={() => setFilter('outside')}>名簿にいない {outsideCount}</button>
            )}
          </div>
          {cols.active && (
            <button type="button" className="ws-link-btn" onClick={cols.resetAll} title="ドラッグで変えた列の幅を、全部元に戻す">列の幅を戻す</button>
          )}
          <span className="ws-toolbar-gap" />
          <span className="ws-muted">表示</span>
          <div className="ws-seg" role="group" aria-label="出欠の書き方">
            <button type="button" className={seg(marks === 'symbol')} onClick={() => setMarks('symbol')} title="◯ 出席　△ 遅刻　▲ 早退　◎ 公欠　× 欠席">◯△×</button>
            <button type="button" className={seg(marks === 'text')} onClick={() => setMarks('text')} title="出席・遅刻・早退・公欠・欠席">出遅欠</button>
          </div>
          <div className="ws-seg" role="group" aria-label="回の見出し">
            <button type="button" className={seg(heading === 'date')} onClick={() => setHeading('date')}>日付</button>
            <button type="button" className={seg(heading === 'number')} onClick={() => setHeading('number')}>第N回</button>
          </div>
          <div className="ws-seg" role="group" aria-label="文字の大きさ">
            <button type="button" className="ws-seg-btn" onClick={() => changeFont(-1)} disabled={fontStep === 0} aria-label="文字を小さく">－</button>
            <span className="ws-seg-label">{FONT_LABELS[fontStep]}</span>
            <button type="button" className="ws-seg-btn" onClick={() => changeFont(1)} disabled={fontStep === FONT_STEPS.length - 1} aria-label="文字を大きく">＋</button>
          </div>
        </div>

        {sessions.length === 0 ? (
          <p className="ws-muted" style={{ padding: '1.2rem' }}>まだ実施した回がありません。</p>
        ) : (
          <div className="ws-table-box">
            <table ref={cols.tableRef} className={'ws-mx ' + cols.className}
                   style={{ fontSize: FONT_STEPS[fontStep] + 'rem', ...(cols.tableStyle || {}) }}>
              {cols.colgroup}
              <thead>
                <tr>
                  <th className="ws-pin0">学籍番号<ColGrip cols={cols} k="id" label="学籍番号" /></th>
                  <th className="ws-pin1" style={pin1}>氏名<ColGrip cols={cols} k="name" label="氏名" /></th>
                  <th className="ws-num">出席率<ColGrip cols={cols} k="rate" label="出席率" /></th>
                  <th className="ws-num ws-sep">欠席<ColGrip cols={cols} k="abs" label="欠席" /></th>
                  {sessions.map(s => (
                    <th key={s.id} className="ws-c" title={fmtDate(s.date) + '（第' + s.no + '回）'}>
                      {heading === 'number' ? '第' + s.no + '回' : fmtMD(s.date)}
                    <ColGrip cols={cols} k="ses" label="回の列（全部）" /></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map(stu => {
                  const isRisk = risk.has(stu.id);
                  const outside = hasRoster && stu.is_outside_roster;
                  const rowCls = (stu.id === studentId ? 'is-sel' : isRisk ? 'is-risk' : '');
                  return (
                    <tr key={stu.id} className={rowCls} ref={el => { rowRefs.current[stu.id] = el; }}>
                      <td className="ws-pin0 ws-muted">{stu.student_id}</td>
                      <td className="ws-pin1" style={pin1}>
                        <button type="button" className="ws-name-btn" onClick={() => { setStudentId(stu.id); setCell(null); }}
                                title={outside ? '履修者名簿にいない学生（打刻のみ）' : '押すと右にこの学生のまとめが出ます'}>
                          {stu.name || '（氏名なし）'}{outside ? ' ＊' : ''}
                        </button>
                      </td>
                      <td className="ws-num" style={{ color: stu.rate != null && stu.rate < 80 ? rateColor(stu.rate) : undefined, fontWeight: 700 }}>
                        {stu.rate == null ? '—' : stu.rate + '%'}
                      </td>
                      <td className="ws-num ws-sep">{stu.absent != null ? stu.absent : '—'}</td>
                      {sessions.map(ses => {
                        const st = cellOf(stu, ses);
                        const info = statusInfo(st);
                        const chosen = cell && cell.stuId === stu.id && cell.sesId === ses.id;
                        const changed = Object.prototype.hasOwnProperty.call(over, stu.id + ':' + ses.id);
                        return (
                          <td key={ses.id} className={'ws-c' + (!st ? ' is-abs' : '') + (chosen ? ' is-chosen' : '') + (changed ? ' is-changed' : '')}>
                            <button type="button" onClick={() => pickCell(stu, ses)} style={{ color: info.color, fontWeight: st === 'present' ? 400 : 700 }}
                                    title={`${stu.name || stu.student_id}・第${ses.no}回（${fmtMD(ses.date)}）：${st ? info.label : '記録なし（欠席）'}`}>
                              {mark(st)}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {list.length === 0 && (
                  <tr><td colSpan={4 + sessions.length} className="ws-muted" style={{ padding: '1rem' }}>当てはまる学生がいません。</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td className="ws-pin0 ws-muted" colSpan={2}>出席人数（{enrolledCount}人中）</td>
                  <td />
                  <td className="ws-sep" />
                  {totals.map((t, i) => <td key={sessions[i].id} className="ws-c ws-muted">{t}</td>)}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div className="ws-mx-legend">
          {['present', 'late', 'early_leave', 'excused', 'absent', 'irregular'].map(k => {
            const i = statusInfo(k);
            return <span key={k}><b style={{ color: i.color }}>{marks === 'symbol' ? i.symbol : i.short}</b> {i.label}</span>;
          })}
          <span>＊ 履修者名簿にいない学生</span>
          <span className="ws-muted">この表の書き方・見出しのまま、出席簿として書き出されます</span>
        </div>
      </div>

      <aside className="ws-split-side" aria-label="選んだ学生">
        {!selStu ? (
          <div className="ws-muted" style={{ lineHeight: 1.9 }}>
            <p style={{ marginTop: 0 }}>名前を押すと、その学生の出欠のまとめがここに出ます。</p>
            {!readOnly && <p>マスを押すと、その回の出欠をここで直せます。まとめて直すときは
              <Link to={'/classes/' + classId + '/edit'}>出欠の確認・修正</Link> を使ってください。</p>}
            {readOnly && <p style={{ color: '#1d4ed8' }}>この授業は年度の締めが済んでいるため、出欠は直せません（見るだけ）。</p>}
          </div>
        ) : (
          <>
            <div>
              <div className="ws-muted" style={{ fontSize: '0.8rem' }}>{selStu.student_id}{selStu.is_placeholder ? '・アプリ未登録' : ''}</div>
              <h2 className="ws-side-title">{selStu.name || '（氏名なし）'}</h2>
            </div>
            <div className="ws-side-rate">
              <strong style={{ color: rateColor(selStu.rate) }}>{selStu.rate == null ? '—' : selStu.rate + '%'}</strong>
              <span className="ws-muted">
                出席 {selCounts.present}・遅刻 {selCounts.late}・早退 {selCounts.early_leave}
                {selCounts.excused > 0 && '・公欠 ' + selCounts.excused}・欠席 {selCounts.absent}
              </span>
            </div>
            {selRisk && <div className="ws-note ws-note-risk">{selRisk.reasons.join(' / ')}</div>}
            {hasRoster && selStu.is_outside_roster && (
              <div className="ws-note ws-note-warn">履修者名簿にいない学生です。概要の「片付けが必要なこと」から、履修者に加える・なかったことにする・別の授業へ移す、を選べます。</div>
            )}

            {cell && selSes && selCellStu && selCellStu.id === selStu.id ? (
              <div className="ws-edit-box">
                <div style={{ fontWeight: 700 }}>第{selSes.no}回（{fmtMD(selSes.date)}）の出欠を直す</div>
                <div className="ws-muted" style={{ fontSize: '0.85rem' }}>
                  いま：<b style={{ color: statusInfo(now).color }}>{now ? statusInfo(now).label : '記録なし（欠席）'}</b>
                </div>
                <div className="ws-edit-choices">
                  {CHOICES.map(v => {
                    const i = statusInfo(v);
                    const same = v === now || (v === 'absent' && !now);
                    return (
                      <button key={v} type="button" disabled={busy || same} onClick={() => save(v)}
                              className="ws-choice" style={same ? undefined : { borderColor: i.color, color: i.color }}>
                        {i.symbol} {i.label}
                      </button>
                    );
                  })}
                </div>
                <input value={reason} onChange={e => setReason(e.target.value)} maxLength={200}
                       placeholder="理由（任意・記録に残る）" aria-label="直す理由" />
                <div className="ws-muted" style={{ fontSize: '0.78rem' }}>
                  直した記録は「手動」になり、誰がいつ直したかが残ります。Esc でやめます。
                </div>
              </div>
            ) : (
              !readOnly && <p className="ws-muted" style={{ fontSize: '0.85rem', margin: 0 }}>この学生の行のマスを押すと、ここで出欠を直せます。</p>
            )}

            <div className="ws-side-actions">
              <Link className="btn-secondary" to={'/classes/' + classId + '/edit'}>出欠の確認・修正（まとめて直す）</Link>
              <Link className="btn-secondary" to={'/students?classId=' + classId}>学生管理で名簿を開く</Link>
              <button type="button" className="btn-secondary" onClick={() => { setStudentId(null); setCell(null); }}>閉じる</button>
            </div>
          </>
        )}
      </aside>
    </WsSplit>
  );
}
